// 인증 서비스
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// 사용자 로그인 (이메일 기반)
export const loginUser = async (email, password) => {
  try {
    // Firestore에서 사용자 정보 조회
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', (email || '').toLowerCase()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      throw new Error('존재하지 않는 사용자입니다.');
    }

    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();

    // 비밀번호가 없는 경우 (최초 로그인 필요)
    if (!userData.password || userData.password === '') {
      throw new Error('비밀번호가 설정되지 않았습니다. 최초 로그인을 진행해주세요.');
    }

    // 비밀번호 확인 (실제로는 해시된 비밀번호와 비교해야 함)
    if (userData.password !== password) {
      throw new Error('비밀번호가 일치하지 않습니다.');
    }

    // 로그인 성공 시 반환할 사용자 정보
    const result = {
      id: userDoc.id, // 문서 ID 추가
      name: userData.name,
      role: userData.role,
      email: userData.email,
      password: userData.password,
      requiresPasswordChange: userData.requiresPasswordChange || false
    };

    return result;
  } catch (error) {
    console.error('로그인 오류:', error);
    throw error;
  }
};

// 비밀번호 해싱 (실제로는 서버에서 해야 함)
export const hashPassword = (password) => {
  // 간단한 해싱 (실제로는 bcrypt 등을 사용)
  return btoa(password);
};

// 로그인 상태 확인
export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('사용자 정보 파싱 오류:', error);
    return null;
  }
};

// 로그아웃
export const logoutUser = () => {
  localStorage.removeItem('user');
};

// 초대코드 검증 (새로운 시스템)
export const verifyInviteCodeAndEmail = async (email, inviteCode) => {
  try {
    const { verifyInviteCode, verifyEmailDomain } = await import('./firestoreService');
    
    // 이메일 도메인 검증 (elicer.com만 허용)
    if (!verifyEmailDomain(email)) {
      throw new Error('elicer.com 이메일만 사용 가능합니다.');
    }
    
    // 초대코드 검증
    const isValidCode = await verifyInviteCode(inviteCode);
    if (!isValidCode) {
      throw new Error('유효하지 않은 초대코드입니다.');
    }
    
    return true;
  } catch (error) {
    console.error('초대코드 및 이메일 검증 오류:', error);
    throw error;
  }
};

// 초대코드로 튜터 계정 생성
export const createTutorAccountWithInviteCode = async (email, inviteCode) => {
  try {
    // 초대코드 및 이메일 검증
    await verifyInviteCodeAndEmail(email, inviteCode);
    
    // 이미 존재하는 사용자인지 확인
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // 이미 존재하는 계정이면 해당 계정 정보 반환
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      
      return {
        id: userDoc.id, // 문서 ID 추가
        name: userData.name,
        role: userData.role,
        email: userData.email,
        password: userData.password || ''
      };
    }
    
    // 새로운 튜터 계정 생성
    const tutorData = {
      name: email.split('@')[0], // 이메일 앞부분을 임시 이름으로 사용
      email: email.toLowerCase(),
      role: 'tutor',
      password: '', // 초기 비밀번호는 빈 값
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    // Firestore에 저장
    const { addDoc, serverTimestamp } = await import('firebase/firestore');
    
    const docRef = await addDoc(usersRef, {
      ...tutorData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return {
      id: docRef.id,
      ...tutorData
    };
  } catch (error) {
    console.error('초대코드로 튜터 계정 생성 오류:', error);
    throw error;
  }
};

// 기존 튜터 계정 조회
export const getExistingTutorAccount = async (email) => {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('등록되지 않은 이메일입니다. 관리자에게 문의하세요.');
    }
    
    const userDoc = querySnapshot.docs[0];
    const userData = userDoc.data();
    
    return {
      id: userDoc.id, // 문서 ID 추가
      name: userData.name,
      role: userData.role,
      email: userData.email,
      password: userData.password || ''
    };
  } catch (error) {
    console.error('튜터 계정 조회 오류:', error);
    throw error;
  }
};

// 튜터 계정 생성
export const createTutorAccount = async (email) => {
  try {
    // 이미 존재하는 사용자인지 확인
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // 이미 존재하는 계정이면 해당 계정 정보 반환
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      
      return {
        id: userDoc.id,
        name: userData.name,
        role: userData.role,
        email: userData.email,
        password: userData.password || ''
      };
    }
    
    // 등록된 이메일인지 확인 (관리자가 미리 등록해둔 이메일)
    const registeredEmailsRef = collection(db, 'registeredEmails');
    const emailQuery = query(registeredEmailsRef, where('email', '==', email.toLowerCase()));
    const emailSnapshot = await getDocs(emailQuery);
    
    if (emailSnapshot.empty) {
      throw new Error('등록되지 않은 이메일입니다. 관리자에게 문의하세요.');
    }
    
    // 튜터 계정 생성
    const tutorData = {
      name: email.split('@')[0], // 이메일 앞부분을 임시 이름으로 사용
      email: email.toLowerCase(),
      role: 'tutor',
      password: '', // 초기 비밀번호는 빈 값
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    // Firestore에 저장
    const { addDoc, serverTimestamp } = await import('firebase/firestore');
    
    const docRef = await addDoc(usersRef, {
      ...tutorData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return {
      id: docRef.id,
      ...tutorData
    };
  } catch (error) {
    console.error('튜터 계정 생성 오류:', error);
    throw error;
  }
}; 